/**
 * API calls for live view, takeover, hand-back and stop.
 *
 * Backend (apps/api/flow_api/routers/live.py, jobs.py):
 *   GET    /jobs/{id}/live/state             viewer and above (no screen content)
 *   POST   /jobs/{id}/live/sessions          supervisor, entity admin
 *   DELETE /jobs/{id}/live/sessions/{sid}    supervisor, entity admin
 *   GET    /jobs/{id}/live/frame             supervisor, entity admin — long-poll
 *   POST   /jobs/{id}/live/input             the session that holds control
 *   POST   /jobs/{id}/takeover               supervisor, entity admin
 *   POST   /jobs/{id}/handback               the controller (or an admin)
 *   POST   /jobs/{id}/cancel                 supervisor, entity admin
 *
 * What goes through TanStack Query and what does not
 * --------------------------------------------------
 * Control state and the three deliberate actions (take control, hand
 * back, stop) are ordinary queries / mutations.
 *
 * Screen frames and input events are NOT. A frame is a picture of a
 * payer portal (PHI); an input event can be a password. Both are
 * fetched / sent with plain `apiFetch` calls and never enter the query
 * or mutation cache (which keeps the last response and the last
 * mutation's variables in memory and shows them in the devtools).
 * Nothing here writes to localStorage, sessionStorage or IndexedDB.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/apiClient";

/** One poll every 3s: 20 a minute, alongside the screen's other polls. */
export const LIVE_STATE_POLL_MS = 3_000;

export interface LiveState {
  jobId: string;
  /** API status (`running`, `waiting_for_human`, …). */
  status: string;
  /** A runner holds the job. */
  onRunner: boolean;
  /** The live relay is deployed in this environment. */
  liveConfigured: boolean;
  canView: boolean;
  canControl: boolean;
  stopRequested: boolean;
  /** False for a smoke run: there is no agent loop to pause. */
  takeoverSupported: boolean;
  takeover: {
    /** Requested and not handed back (it may have timed out). */
    held: boolean;
    /** Held and inside its time limit. */
    live: boolean;
    by: { id: string; name: string } | null;
    byMe: boolean;
    /** The controlling view session; only sent to the controller. */
    sessionId: string | null;
    expiresAt: string | null;
    remainingSec: number;
  };
  viewport: { width: number; height: number };
  /**
   * Why the runner asked for a person, as a fixed code (for example
   * `captcha_encountered`). Null when the job is not waiting or taken
   * over. Absent from an older API.
   */
  helpReason?: string | null;
}

export interface LiveSession {
  sessionId: string;
  expiresAt: string;
  viewport: { width: number; height: number };
  runnerConnected: boolean;
}

/** What the runner is doing, as reported with each frame. */
export type RunnerMode = "agent" | "waiting_for_human" | "human" | "withheld";

export interface LiveFrameResponse {
  frame: { seq: number; ageMs: number; jpegBase64: string } | null;
  mode: RunnerMode | null;
  runnerConnected: boolean;
}

/** Mirrors `InputEvent` in flow_shared_core/live_protocol.py. */
export type LiveInputEvent =
  | { type: "mouse_move"; x: number; y: number }
  | { type: "mouse_down" | "mouse_up"; x: number; y: number; button: "left" | "middle" | "right" }
  | { type: "wheel"; x: number; y: number; delta_x: number; delta_y: number }
  | { type: "key"; key: string }
  | { type: "text"; text: string };

function isFinal(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 400 && error.status < 500;
}

export function useLiveState(jobId: string | undefined, enabled: boolean) {
  return useQuery<LiveState>({
    queryKey: ["jobs", jobId, "live-state"],
    queryFn: () => apiFetch<LiveState>(`/jobs/${jobId}/live/state`),
    enabled: Boolean(jobId) && enabled,
    staleTime: 1_000,
    retry: (count, error) => !isFinal(error) && count < 2,
    refetchInterval: (query) => (enabled && !isFinal(query.state.error) ? LIVE_STATE_POLL_MS : false),
  });
}

// ── view session and frames: plain calls, never cached ───────

export function openLiveSession(jobId: string): Promise<LiveSession> {
  return apiFetch<LiveSession>(`/jobs/${jobId}/live/sessions`, { method: "POST" });
}

export function closeLiveSession(jobId: string, sessionId: string): Promise<unknown> {
  return apiFetch(`/jobs/${jobId}/live/sessions/${sessionId}`, { method: "DELETE" });
}

export function fetchLiveFrame(
  jobId: string,
  sessionId: string,
  after: number,
  signal?: AbortSignal,
): Promise<LiveFrameResponse> {
  const query = new URLSearchParams({ session: sessionId, after: String(after) });
  return apiFetch<LiveFrameResponse>(`/jobs/${jobId}/live/frame?${query.toString()}`, { signal });
}

export function sendLiveInput(
  jobId: string,
  sessionId: string,
  events: LiveInputEvent[],
): Promise<{ accepted: number; received: number }> {
  return apiFetch(`/jobs/${jobId}/live/input`, {
    method: "POST",
    body: JSON.stringify({ session_id: sessionId, events }),
  });
}

// ── deliberate actions ───────────────────────────────────────

export function useTakeControl(jobId: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string; takeoverSeq: number }, Error, { sessionId: string }>({
    mutationFn: ({ sessionId }) =>
      apiFetch(`/jobs/${jobId}/takeover`, {
        method: "POST",
        body: JSON.stringify({ session_id: sessionId }),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["jobs", jobId] }),
  });
}

export type HandbackDecision = "continue" | "end";

export function useHandBack(jobId: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string; decision: HandbackDecision }, Error, HandbackDecision>({
    mutationFn: (decision) =>
      apiFetch(`/jobs/${jobId}/handback`, { method: "POST", body: JSON.stringify({ decision }) }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["jobs", jobId] }),
  });
}

/** `cancelled` for a job that had not started, `stopping` for one on a runner. */
export function useStopJob(jobId: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string; status: "cancelled" | "stopping" }, Error, void>({
    mutationFn: () => apiFetch(`/jobs/${jobId}/cancel`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["jobs"] }),
  });
}
